import { validateApiKey, validateChannelInput, sanitizeText } from "../classifier";
import React, { useState } from "react";

const LIMIT_OPTS = [
  { value: 50, label: "50 videos" }, { value: 100, label: "100 videos" },
  { value: 250, label: "250 videos" }, { value: 500, label: "500 videos" },
  { value: 1000, label: "1,000 videos" }, { value: 0, label: "All videos" },
];

const overlay = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex",
  alignItems: "flex-start", justifyContent: "center", paddingTop: 60, zIndex: 200,
};
const box = {
  background: "#1e293b", border: "1px solid #334155", borderRadius: 14,
  padding: 28, width: 480, maxWidth: "93%", boxSizing: "border-box",
};
const inp = {
  width: "100%", padding: "9px 12px", background: "#0f172a", border: "1px solid #334155",
  borderRadius: 8, color: "#e2e8f0", fontFamily: "inherit", fontSize: 13, outline: "none",
};
const btn = (primary) => ({
  padding: "9px 20px", border: `1px solid ${primary ? "#2563eb" : "#334155"}`,
  borderRadius: 8, background: primary ? "#2563eb" : "none",
  color: primary ? "#fff" : "#94a3b8", fontFamily: "inherit", fontSize: 13,
  fontWeight: primary ? 700 : 400, cursor: "pointer",
});

export function AddChannelModal({ onAdd, onClose, loading }) {
  const [name, setName] = useState("");
  const [ytId, setYtId] = useState("");
  const [apiKey, setApiKey] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [validationError, setValidationError] = useState(null);

  const handleSubmit = () => {
    setValidationError(null);
    if (!name.trim()) { setValidationError("Channel label is required."); return; }
    const keyCheck = validateApiKey(apiKey);
    if (!keyCheck.valid) { setValidationError(keyCheck.error); return; }
    const idCheck = validateChannelInput(ytId);
    if (!idCheck.valid) { setValidationError(idCheck.error); return; }
    onAdd(sanitizeText(name, 100), idCheck.value, keyCheck.value);
  };

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 22 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#f1f5f9" }}>Add channel</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", fontSize: 20, cursor: "pointer", padding: 4 }}>×</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 6 }}>Channel label</label>
            <input type="text" style={inp} placeholder="e.g. Doomer Friday" value={name} onChange={e => setName(e.target.value)} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 6 }}>Channel ID or @handle</label>
            <input type="text" style={inp} placeholder="@handle or UCxxxxxxxx" value={ytId} onChange={e => setYtId(e.target.value)} />
            <div style={{ fontSize: 11, color: "#475569", marginTop: 5 }}>
              YouTube Studio → Settings → Channel → Advanced settings
            </div>
          </div>
          <div>
            <label style={{ fontSize: 12, color: "#94a3b8", display: "block", marginBottom: 6 }}>YouTube Data API key</label>
            <input type="password" style={inp} placeholder="AIzaSy..." value={apiKey} onChange={e => setApiKey(e.target.value)} />
            <div style={{ marginTop: 8, background: "rgba(43,94,167,0.08)", border: "1px solid rgba(43,94,167,0.25)", borderRadius: 6, padding: "10px 12px" }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#2B5EA7", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 6 }}>
                How to get a free API key
              </div>
              {[
                { n:"1", text:"Go to", link:"console.cloud.google.com", url:"https://console.cloud.google.com" },
                { n:"2", text:"Create a project (or select existing)" },
                { n:"3", text:"APIs & Services → Enable APIs → search YouTube Data API v3 → Enable" },
                { n:"4", text:"APIs & Services → Credentials → Create Credentials → API key" },
                { n:"5", text:"Copy the key and paste it above" },
              ].map(step => (
                <div key={step.n} style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 4 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#2B5EA7", flexShrink: 0, marginTop: 1 }}>{step.n}</span>
                  <span style={{ fontSize: 11, color: "#5A6A7E", lineHeight: 1.5 }}>
                    {step.text}{" "}
                    {step.link && <a href={step.url} target="_blank" rel="noreferrer" style={{ color: "#60a5fa" }}>{step.link} ↗</a>}
                  </span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
            <button style={{ ...btn(true), flex: 1, opacity: loading ? 0.6 : 1 }}
              disabled={loading || !name || !ytId || !apiKey}
              onClick={handleSubmit}>
              {loading ? "Checking channel..." : "Add channel →"}
            </button>
            <button style={btn(false)} onClick={onClose}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function FetchConfirmModal({ data, currentCount, onConfirm, onClose }) {
  const [limit, setLimit] = useState(500);
  const [mode, setMode] = useState("preset");
  const [custom, setCustom] = useState("");
  const { channel, totalVideos } = data;

  const resolvedLimit = mode === "custom" ? (parseInt(custom) || 0) : limit;
  const willFetch = resolvedLimit === 0 || resolvedLimit >= totalVideos ? totalVideos : resolvedLimit;
  const pages = Math.ceil(willFetch / 50);
  const cost = pages * 2;

  return (
    <div style={overlay} onClick={onClose}>
      <div style={box} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 17, fontWeight: 700, color: "#f1f5f9" }}>Confirm video fetch</div>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "#64748b", fontSize: 20, cursor: "pointer", padding: 4 }}>×</button>
        </div>
        <div style={{ fontSize: 13, color: "#64748b", marginBottom: 18 }}>{channel.title || channel.name}</div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
          {[["Videos on channel", totalVideos?.toLocaleString()], ["Currently loaded", currentCount.toLocaleString()]].map(([l, v]) => (
            <div key={l} style={{ background: "#0f172a", borderRadius: 8, padding: "12px 14px", border: "1px solid #334155" }}>
              <div style={{ fontSize: 11, color: "#64748b", marginBottom: 5 }}>{l}</div>
              <div style={{ fontSize: 22, fontWeight: 700, fontVariantNumeric: "tabular-nums", color: "#f1f5f9" }}>{v}</div>
            </div>
          ))}
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>How many videos to fetch?</div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {LIMIT_OPTS.map(opt => (
              <button key={opt.value} onClick={() => { setMode("preset"); setLimit(opt.value); }}
                style={{ padding: "5px 12px", border: `1px solid ${mode === "preset" && limit === opt.value ? "#2563eb" : "#334155"}`, borderRadius: 6, background: mode === "preset" && limit === opt.value ? "#172554" : "none", color: mode === "preset" && limit === opt.value ? "#60a5fa" : "#64748b", fontSize: 12, cursor: "pointer", fontWeight: mode === "preset" && limit === opt.value ? 700 : 400 }}>
                {opt.label}
              </button>
            ))}
            <button onClick={() => setMode("custom")} style={{ padding: "5px 12px", border: `1px solid ${mode === "custom" ? "#2563eb" : "#334155"}`, borderRadius: 6, background: mode === "custom" ? "#172554" : "none", color: mode === "custom" ? "#60a5fa" : "#64748b", fontSize: 12, cursor: "pointer" }}>
              Custom
            </button>
          </div>
          {mode === "custom" && (
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <input type="number" min={1} style={{ ...inp, width: 160 }} placeholder="Enter number" value={custom} onChange={e => setCustom(e.target.value)} />
              <span style={{ fontSize: 12, color: "#64748b" }}>videos</span>
            </div>
          )}
        </div>

        <div style={{ background: "#0f172a", borderRadius: 8, padding: "12px 14px", marginBottom: 18, border: "1px solid #334155" }}>
          <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10, color: "#e2e8f0" }}>Fetching {willFetch.toLocaleString()} of {totalVideos?.toLocaleString()} videos</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[["Playlist pages", pages + " units"], ["Video detail calls", pages + " units"], ["Total API cost", cost + " / 10,000"]].map(([l, v]) => (
              <div key={l} style={{ background: "#1e293b", borderRadius: 6, padding: "8px 10px", border: "1px solid #334155" }}>
                <div style={{ fontSize: 10, color: "#475569", marginBottom: 3 }}>{l}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button style={{ ...btn(true), flex: 1 }} onClick={() => onConfirm(resolvedLimit)}>Fetch videos →</button>
          <button style={btn(false)} onClick={onClose}>Cancel</button>
        </div>
      </div>
    </div>
  );
}
